import React, { useCallback, useEffect, useState } from "react";
import { useParams, useLocation, useNavigate } from "react-router-dom";
import { FaEdit, FaTrash, FaSave, FaTimes } from "react-icons/fa";
import { apiCall } from "../main/Apicall";
import Pagination from "../Reusablecomponents/Pagination";
import ToastPopup from "../Popups/ToastPopup";
import EditQuestionModal from "../Popups/EditQuestionModal";
import "../css/others/Table.css";
import { ControlVisibility, createToastMessage, ToastMessage } from "../Interfaces/Interface";
const ChecklistDetails: React.FC = () => {
  const { templateId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const { isIntegratedChecklist, controls, status } = location.state || { isIntegratedChecklist: false, controls: [], status: "DRAFT" };
  const [details, setDetails] = useState<any>({ content: [] });
  const [, setLoading] = useState<boolean>(true);
  const [newQuestions, setNewQuestions] = useState<any[]>([]);
  const [editableQuestion, setEditableQuestion] = useState<any>(null);
  const [isEditModalOpen, setIsEditModalOpen] = useState<boolean>(false);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [itemsPerPage, setItemsPerPage] = useState<number>(10);
  const [totalElements, setTotalElements] = useState<number>(0);
  const [totalPages, setTotalPages] = useState<number>(0);
  const [message, setMessage] = useState<ToastMessage | null>(null);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState<boolean>(false);
  const [selectedQuestionId, setSelectedQuestionId] = useState<string | null>(null);
  const [showRedirectPopup, setShowRedirectPopup] = useState<boolean>(false);
  const [savingQuestionId, setSavingQuestionId] = useState<number | null>(null);
  const [controlVisibility, setControlVisibility] = useState<ControlVisibility>({ pims: controls.includes("PIMS"), isms: controls.includes("ISMS"), bcms: controls.includes("BCMS"), cmmi: controls.includes("CMMI"), soc1: controls.includes("SOC1"), soc2: controls.includes("SOC2"), qms: controls.includes("QMS"), aims: controls.includes("AIMS"), erm: controls.includes("ERM"), itsm: controls.includes("ITSM"), ers: controls.includes("ERS"), ohs: controls.includes("OHS") });
  useEffect(() => {
    if (status === "ACTIVE") {
      const timer = setTimeout(() => {
        setShowRedirectPopup(true);
      }, 1000);
      return () => clearTimeout(timer);
    }
  }, [status]);
  useEffect(() => {
    if (showRedirectPopup) {
      const timer = setTimeout(() => {
        navigate(-1);
      }, 2000);
      return () => clearTimeout(timer);
    }
  }, [showRedirectPopup, navigate]);
  useEffect(() => {
    setControlVisibility({ pims: controls.includes("PIMS"), isms: controls.includes("ISMS"), bcms: controls.includes("BCMS"), cmmi: controls.includes("CMMI"), soc1: controls.includes("SOC1"), soc2: controls.includes("SOC2"), qms: controls.includes("QMS"), aims: controls.includes("AIMS"), erm: controls.includes("ERM"), itsm: controls.includes("ITSM"), ers: controls.includes("ERS"), ohs: controls.includes("OHS") });
  }, [controls]);
  const fetchChecklistDetails = useCallback(async () => {
    if (!templateId) return;
    setLoading(true);
    try {
      const checklist = await apiCall("get", `/api/checklist/templates/${templateId}/questions/edit`, undefined, { page: currentPage - 1, size: itemsPerPage });
      setDetails(checklist || { content: [] });
      setTotalElements(checklist.totalElements || 0);
      setTotalPages(checklist.totalPages || 0);
    } catch (error) {
      console.error("Error fetching checklist details:", error);
    } finally {
      setLoading(false);
    }
  }, [templateId, currentPage, itemsPerPage]);
  useEffect(() => {
    fetchChecklistDetails();
  }, [fetchChecklistDetails]);
  const getActualFieldName = (displayName: string): string => {
    const fieldMap: { [key: string]: string } = { "PIMS": "pims", "ISMS": "isms", "BCMS": "bcms", "CMMI": "cmmi", "AIMS": "aims", "SOC1": "soc1", "SOC2": "soc2", "QMS": "qms", "ERM": "erm", "ITSM": "itsm", "ERS": "ers", "OHS": "ohs" };
    return fieldMap[displayName] || displayName.toLowerCase().replace(/\s+/g, '');
  };
  const restrictedChars = /[\\:|/]/;
  const containsSpecialChars = (str: string) => {
    return restrictedChars.test(str);
  };
  const handleRestrictedInput = (value: string, updateFn: () => void) => {
    if (containsSpecialChars(value)) {
      setMessage(createToastMessage(`Special characters are not allowed - ${restrictedChars}`, "warning", 3000));
      return;
    }
    updateFn();
  };
  const handleAddRow = () => {
    const defaultControlValues: { [key: string]: string } = {};
    controls.forEach((control: string) => {
      const actualFieldName = getActualFieldName(control);
      defaultControlValues[actualFieldName] = "";
    });
    const newRow = { questionId: `new-${Date.now()}`, questionText: "", expectedArtifact: "", practiceArea: "", ...defaultControlValues, isNew: true };
    setNewQuestions((prev) => [...prev, newRow]);
  };
  const validateQuestion = (question: any) => {
    if (!question.questionText.trim() || !question.expectedArtifact.trim()) {
      return false;
    }
    return true;
  };
  const handleSaveSingleNewQuestion = async (index: number) => {
    const question = newQuestions[index];
    if (!validateQuestion(question)) {
      setMessage(createToastMessage("Please fill all required fields (Question and Expected Artifact)", "warning", 3000));
      return;
    }
    setSavingQuestionId(question.questionId);
    try {
      const questionData: any = { questionText: question.questionText || "", expectedArtifact: question.expectedArtifact || "", practiceArea: question.practiceArea || "" };
      if (isIntegratedChecklist) {
        controls.forEach((control: string) => {
          const actualFieldName = getActualFieldName(control);
          questionData[actualFieldName] = question[actualFieldName] || "";
        });
      }
      await apiCall("post", `/api/checklist/templates/${templateId}/questions`, [questionData]);
      setMessage(createToastMessage("Question added successfully", "success", 3000));
      setNewQuestions((prev) => prev.filter((_, i) => i !== index));
      await fetchChecklistDetails();
    } catch (error: any) {
      const errorMessage = error?.response?.data?.message || "Error adding question";
      setMessage(createToastMessage(errorMessage, "error", 3000));
    } finally {
      setSavingQuestionId(null);
    }
  };
  const handleRemoveRow = (id: string) => {
    const isNew = newQuestions.some((q) => q.questionId === id);
    if (isNew) {
      setNewQuestions((prev) => prev.filter((q) => q.questionId !== id));
    } else {
      setSelectedQuestionId(id);
      setIsDeleteModalOpen(true);
    }
  };
  const confirmDelete = async () => {
    if (!templateId || !selectedQuestionId) return;
    try {
      await apiCall("delete", `/api/checklist/templates/${templateId}/questions/${selectedQuestionId}`);
      setMessage(createToastMessage("Question deleted successfully", "success", 3000));
      await fetchChecklistDetails();
    } catch (error: any) {
      const errorMessage = error?.response?.data?.message || "Error deleting question";
      setMessage(createToastMessage(errorMessage, "error", 3000));
    } finally {
      setIsDeleteModalOpen(false);
      setSelectedQuestionId(null);
    }
  };
  const handleEditClick = (id: string) => {
    const questionToEdit = details?.content.find((item: any) => item.questionId.toString() === id);
    if (questionToEdit) {
      setEditableQuestion(questionToEdit);
      setIsEditModalOpen(true);
    }
  };
  const handleModalChange = (e: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>) => {
    const { name, value } = e.target;
    if (containsSpecialChars(value)) {
      setMessage(createToastMessage(`Special characters are not allowed - ${restrictedChars}`, "warning", 3000));
      return;
    }
    setEditableQuestion((prev: any) => ({ ...prev, [name]: value }));
  };
  const handleSaveChanges = async () => {
    if (editableQuestion && templateId) {
      if (!editableQuestion.questionText.trim() || !editableQuestion.expectedArtifact.trim()) {
        setMessage(createToastMessage("Please fill all required fields", "warning", 3000));
        return;
      }
      try {
        const questionData: any = { questionId: editableQuestion.questionId, questionText: editableQuestion.questionText || "", expectedArtifact: editableQuestion.expectedArtifact || "", practiceArea: editableQuestion.practiceArea || "" };
        if (isIntegratedChecklist) {
          controls.forEach((control: string) => {
            const actualFieldName = getActualFieldName(control);
            questionData[actualFieldName] = editableQuestion[actualFieldName] || "";
          });
        }
        await apiCall("put", `/api/checklist/templates/${templateId}/questions`, [questionData]);
        setMessage(createToastMessage("Question updated successfully", "success", 3000));
        setIsEditModalOpen(false);
        await fetchChecklistDetails();
      } catch (error: any) {
        const errorMessage = error?.response?.data?.message || "Error updating question";
        setMessage(createToastMessage(errorMessage, "error", 3000));
      }
    }
  };
  const handleCancelNewQuestion = (index: number) => {
    setNewQuestions((prev) => prev.filter((_, i) => i !== index));
  };
  const columnCount = () => {
    let count = 4;
    if (!isIntegratedChecklist) {
      count += 1;
    }
    if (isIntegratedChecklist) {
      count += Object.values(controlVisibility).filter(visible => visible).length;
    }
    return count;
  };
  const controlFields = [{ key: "pims", label: "PIMS", visible: controlVisibility.pims }, { key: "isms", label: "ISMS", visible: controlVisibility.isms }, { key: "bcms", label: "BCMS", visible: controlVisibility.bcms }, { key: "cmmi", label: "CMMI", visible: controlVisibility.cmmi }, { key: "soc1", label: "SOC1", visible: controlVisibility.soc1 }, { key: "soc2", label: "SOC2", visible: controlVisibility.soc2 }, { key: "qms", label: "QMS", visible: controlVisibility.qms }, { key: "aims", label: "AIMS", visible: controlVisibility.aims }, { key: "erm", label: "ERM", visible: controlVisibility.erm }, { key: "itsm", label: "ITSM", visible: controlVisibility.itsm }, { key: "ers", label: "ERS", visible: controlVisibility.ers }, { key: "ohs", label: "OHS", visible: controlVisibility.ohs }];
  return (
    <div className="table-details">
      <div className="table-title">
        <h2>Checklist Details</h2>
        <button className="add-question-button" onClick={handleAddRow}>Add Question</button>
      </div>
      <>
        <div className="table-container">
          <div className="table-inner-container">
            <table className="main-table">
              <thead>
                <tr>
                  <th>S.No.</th>
                  <th className="header-width">Question</th>
                  <th className="header-width">Expected Artifact</th>
                  {!isIntegratedChecklist && <th className="header-width">Control/Practice Area</th>}
                  {isIntegratedChecklist && (
                    <>
                      {controlFields
                        .filter(control => control.visible)
                        .map(control => (
                          <th key={control.key} className="header-width">{control.label}</th>
                        ))}
                    </>
                  )}
                  <th>Action</th>
                </tr>
              </thead>
              <tbody>
                {details.content && details.content.length > 0 && (
                  details.content.map((item: any, index: number) => (
                    <tr key={item.questionId}>
                      <td>{(currentPage - 1) * itemsPerPage + index + 1}</td>
                      <td>{item.questionText || "N/A"}</td>
                      <td>{item.expectedArtifact || "N/A"}</td>
                      {!isIntegratedChecklist && <td>{item.practiceArea || "N/A"}</td>}
                      {isIntegratedChecklist && (
                        <>
                          {controlFields
                            .filter(control => control.visible)
                            .map(control => (
                              <td key={control.key}>{item[control.key] || "N/A"}</td>
                            ))}
                        </>
                      )}
                      <td>
                        <FaEdit className="action-icon" onClick={() => handleEditClick(item.questionId.toString())} title="Edit" />
                        <FaTrash className="action-icon delete-icon" onClick={() => handleRemoveRow(item.questionId.toString())} title="Delete" />
                      </td>
                    </tr>
                  ))
                )}
                {newQuestions.map((question, index) => {
                  const isSaving = savingQuestionId === question.questionId;
                  return (
                    <tr key={question.questionId}>
                      <td>{totalElements + index + 1}</td>
                      <td>
                        <textarea value={question.questionText} onChange={(e) => handleRestrictedInput(e.target.value, () => { const updated = [...newQuestions]; updated[index].questionText = e.target.value; setNewQuestions(updated) })} placeholder="Enter new question" disabled={isSaving} />
                      </td>
                      <td>
                        <textarea value={question.expectedArtifact} onChange={(e) => handleRestrictedInput(e.target.value, () => { const updated = [...newQuestions]; updated[index].expectedArtifact = e.target.value; setNewQuestions(updated); })} placeholder="Enter Expected Artifact" disabled={isSaving} />
                      </td>
                      {!isIntegratedChecklist && (
                        <td>
                          <textarea value={question.practiceArea} onChange={(e) => handleRestrictedInput(e.target.value, () => { const updated = [...newQuestions]; updated[index].practiceArea = e.target.value; setNewQuestions(updated); })} placeholder="Enter Control/Practice Area" disabled={isSaving} />
                        </td>
                      )}
                      {isIntegratedChecklist &&
                        controlFields
                          .filter(control => control.visible)
                          .map(control => (
                            <td key={control.key}>
                              <textarea value={question[control.key] || ""} onChange={(e) => handleRestrictedInput(e.target.value, () => { const updated = [...newQuestions]; updated[index][control.key] = e.target.value; setNewQuestions(updated); })} placeholder={control.label} disabled={isSaving} />
                            </td>
                          ))
                      }
                      <td>
                        <FaSave className={`action-icon ${isSaving ? 'disabled' : ''}`} onClick={() => handleSaveSingleNewQuestion(index)} title="Save" style={{ opacity: isSaving ? 0.5 : 1, cursor: isSaving ? 'not-allowed' : 'pointer' }} />
                        <FaTimes className="action-icon" onClick={() => handleCancelNewQuestion(index)} title="Cancel" />
                      </td>
                    </tr>
                  );
                })}
                {details.content?.length === 0 && newQuestions.length === 0 && (
                  <tr>
                    <td colSpan={columnCount()} className="no-data">No questions available for this checklist.</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
        <Pagination
          currentPage={currentPage}
          totalPages={totalPages}
          totalElements={totalElements}
          itemsPerPage={itemsPerPage}
          onPageChange={setCurrentPage}
          onItemsPerPageChange={setItemsPerPage}
        />
      </>
      <EditQuestionModal
        isOpen={isEditModalOpen}
        question={editableQuestion}
        isIntegratedChecklist={isIntegratedChecklist}
        controls={controls}
        controlVisibility={controlVisibility}
        onChange={handleModalChange}
        onSave={handleSaveChanges}
        onClose={() => setIsEditModalOpen(false)}
      />
      {isDeleteModalOpen && (
        <div className="modal-overlay">
          <div className="modal-content">
            <h3>Confirm Deletion</h3>
            <p>Are you sure you want to delete this question?</p>
            <div className="buttons">
              <button className="cancelbutton" onClick={() => setIsDeleteModalOpen(false)}>Cancel</button>
              <button className="savebutton" onClick={confirmDelete}>Delete</button>
            </div>
          </div>
        </div>
      )}
      {showRedirectPopup && (
        <div className="modal-overlay">
          <div className="modal-content">
            <h3>Redirection Notice</h3>
            <p>This checklist is in ACTIVE status. Redirecting to draft version...</p>
          </div>
        </div>
      )}
      {message && (<ToastPopup message={message.text} type={message.type} duration={message.duration} onClose={() => setMessage(null)} />)}
    </div>
  );
};
export default ChecklistDetails;